import { useEffect, useState } from 'react'
import {
  MUIAutocompleteMultiSelect,
  MUIBox,
  MUIButton,
  MUIField,
  MUISelect,
  MUISelectWithoutControl,
} from '../../../Shared'
import MUIDateTimePicker from '../../../../Components/MUIDateTimePicker'
import { Alert, Grid } from '@mui/material'
import { Control, useForm } from 'react-hook-form'
import { ADVANCED_SEARCH, BULK_STATE_CHANGE_FORM } from '../../../../Constants/Texts'
import BulkStyle from './BulkStateChangeEditorForm.module.scss'
import { useNavigate } from 'react-router-dom'
import CONSTANTS, { PAGES_ROUTES } from '../../../../Constants'
import {
  checkDateTimeError,
  combineErrorMessage,
  createPayloadForSearch,
  fetchItemTypes,
  fetchLayoutForBulkOperation,
  setLayoutsForBulkForm,
} from '../../../../Helpers/Util'
import {
  ADVANCE_SEARCH,
  BULK_OPERATIONS,
  SYSTEM_CONFIG_APIS,
} from '../../../../Constants/ApiDefinations'
import mainApiService from '../../../../Services'
import TipsIcon from '../../../Icons/TipsIcon'
import BulkSearchResult from './BulkSearchResult'
import RoleConfirmation from '../../../Dialogs/RoleConfirmation'
import { RoleConfirmationModal } from '../../../../Models'
import { useAppDispatch } from '../../../../Store/Hooks'
import { showAlert } from '../../../../Store/Slice/alert.slice'
import updateTagMetaData from '../../../../Helpers/UpdateTagMetaData'
import { getLocalStorage } from '../../../../Helpers/Storage'

const BulkStateChangeEditorForm = () => {
  const [selectedFromDateTime, setSelectedFromDateTime] = useState<Date | null>(null)
  const [selectedToDateTime, setSelectedToDateTime] = useState<Date | null>(null)
  const [itemVal, setItemVal] = useState<any[]>([])
  const [itemTypes, setItemTypes] = useState<any[]>([])
  const [layouts, setLayouts] = useState<any[]>([])
  const [selectedTypes, setSelectedTypes] = useState<any[]>([])
  const [selectedLayouts, setSelectedLayouts] = useState<any[]>([])
  const [isTypeDropdownDisabled, setIsTypeDropdownDisabled] = useState(true)
  const [selectedState, setSelectedState] = useState<string>()
  const [isParameterSectionDisabled, setIsParameterSectionDisabled] = useState<boolean>(false)
  const [isSearchButtonEnabled, setIsSearchButtonEnabled] = useState<boolean>(true)
  const [searchPayload, setSearchPayload] = useState<any>({})
  const [dialogProps, setDialogProps] = useState<RoleConfirmationModal>({
    heading: '',
    description: '',
    color: 'primary',
    open: false,
  })
  const [createJobNameBtnName, setCreateJobNameBtnName] = useState<string>('Feature')
  const [selectedRecords, setSelectedRecords] = useState([])
  const [targateStateOption, setTargateStateOption] = useState<any[]>([])

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const {
    control,
    setValue,
    setError,
    clearErrors,
    getValues,
    watch,
    formState: { errors, isDirty },
  } = useForm<any>({ mode: 'onChange' })

  useEffect(() => {
    fetchItemVal()
    updateTagMetaData({ title: 'Create Bulk State Change' })
    fetchTargateStateData()
    return () => {
      updateTagMetaData({})
    }
  }, [])

  useEffect(() => {
    const item = getValues('item')
    if (item) {
      fetchItemTypes(item).then((types) => {
        setItemTypes(types)
        setIsTypeDropdownDisabled(false)
      })
    } else {
      setItemTypes([])
      setIsTypeDropdownDisabled(true)
    }
    if (isDirty) {
      setSelectedTypes([])
      setSelectedLayouts([])
    }
  }, [watch('item')])

  useEffect(() => {
    checkDateTimeError(selectedFromDateTime, selectedToDateTime, setError, clearErrors)
  }, [selectedFromDateTime, selectedToDateTime])

  useEffect(() => {
    if (!getValues('item') || !selectedTypes) return
    fetchLayoutForBulkOperation(selectedTypes, getValues('item'), setLayouts)
  }, [getValues('item'), selectedTypes])

  useEffect(() => {
    if (getValues('jobName') && getValues('targetState') && getValues('item')) {
      setIsSearchButtonEnabled(false)
    } else {
      setIsSearchButtonEnabled(true)
    }
  }, [watch('jobName'), watch('targetState'), watch('item')])

  useEffect(() => {
    setLayoutsForBulkForm(selectedLayouts, layouts, setSelectedLayouts)
  }, [layouts])

  const checkFeatureUnfeatureAccess = () => {
    const getPermission = getLocalStorage(CONSTANTS.LOCAL_KEYS.ROLE_DATA)
    const tollgatesModule = getPermission.find((module: any) => module.key === 'TOLLGATES')

    if (!tollgatesModule) {
      return {
        canFeature: false,
        canUnfeature: false,
      }
    }
    const featureModule = tollgatesModule.submodules.find(
      (submodule: any) => submodule.key === 'FEATURING',
    )
    const unfeatureModule = tollgatesModule.submodules.find(
      (submodule: any) => submodule.key === 'UNFEATURE',
    )

    return {
      canFeature: featureModule?.permission?.canStateTransition || false,
      canUnfeature: unfeatureModule?.permission?.canStateTransition || false,
    }
  }

  const fetchTargateStateData = async () => {
    const requestedData = SYSTEM_CONFIG_APIS.LIST_MASTERS('TARGET_STATE')
    const result: any = await mainApiService(requestedData)
    if (result?.responseCode === 200) {
      result?.data.forEach((item: any) => delete item._id)
      setTargateStateOption(result?.data)
    } else {
      dispatch(showAlert([true, result?.error?.errors?.join(','), 'error']))
    }
  }

  const handleDateTimeChange = (type: 'StartDate' | 'EndDate', newDateTime: Date | null) => {
    if (type === 'StartDate') {
      setSelectedFromDateTime(newDateTime)
    } else if (type === 'EndDate') {
      setSelectedToDateTime(newDateTime)
    }
    handleOnFormChange()
  }

  const onSearch = async (e: any) => {
    e?.preventDefault()
    const payload = createPayloadForSearch(
      getValues,
      selectedFromDateTime,
      selectedToDateTime,
      selectedState,
      selectedTypes,
      selectedLayouts,
    )
    setSearchPayload({ ...payload })
    setIsSearchButtonEnabled(true)
  }

  const fetchItemVal = async () => {
    const requestedData = ADVANCE_SEARCH.LIST_ITEMS()
    try {
      const result: any = await mainApiService(requestedData)
      const itemList = result?.data || []
      setItemVal(itemList)
    } catch (error) {
      console.error('Error fetching item types:', error)
    }
  }

  const handleTargetStateOnChange = (e: any) => {
    const value = e.target.value
    setValue('targetState', value)

    if (value === 'feature') {
      setCreateJobNameBtnName('Feature')
      setSelectedState('ready_for_featuring')
      setSelectedFromDateTime(null)
      setSelectedToDateTime(null)
    } else if (value === 'unfeature') {
      setCreateJobNameBtnName('Unfeature')
      setSelectedState('featured')
    } else {
      setSelectedState('')
    }
    setSearchPayload({})
  }

  const handleCreateJob = () => {
    if (createJobNameBtnName === 'Feature') {
      setDialogProps({
        heading: CONSTANTS.BULK_FEATURE_HEADIMG,
        description: CONSTANTS.BULK_FEATURE_DESC,
        color: 'primary',
        Icon: 'FeaturedIcon',
        open: true,
        iconColor: 'warning',
      })
    } else if (createJobNameBtnName === 'Unfeature') {
      setDialogProps({
        heading: CONSTANTS.BULK_UNFEATURE_HEADIMG,
        description: CONSTANTS.BULK_UNFEATURE_DESC,
        Icon: 'UnFeaturedIcon',
        color: 'error',
        iconColor: 'error',
        open: true,
      })
    }
  }

  const handleConfirmJobCreation = async () => {
    const payload = {
      jobName: getValues('jobName'),
      targetState: getValues('targetState'),
      module: getValues('item'),
      contentInfo: selectedRecords,
    }

    const requestedData = BULK_OPERATIONS.BULK_STATE_CHANGE_CREATE(payload)
    const result = await mainApiService(requestedData)
    if (result?.responseCode === 200) {
      handleClose()
      navigate(`/${PAGES_ROUTES.BulkOperation}/${PAGES_ROUTES.BulkStateListing}`)
      dispatch(showAlert([true, result?.message, 'success']))
    } else {
      handleClose()
      dispatch(showAlert([true, combineErrorMessage(result), 'error']))
    }
  }

  const handleClose = () => {
    setDialogProps({
      heading: '',
      description: '',
      color: 'primary',
      open: false,
    })
  }

  const handleButtonDisableByRole = () => {
    let isDisabled = true
    if (createJobNameBtnName === 'Feature') {
      isDisabled = !checkFeatureUnfeatureAccess().canFeature
    }
    if (createJobNameBtnName === 'Unfeature') {
      isDisabled = !checkFeatureUnfeatureAccess().canUnfeature
    }

    return isDisabled
  }

  const handleOnFormChange = () => {
    if (getValues('jobName') && getValues('targetState') && getValues('item')) {
      setIsSearchButtonEnabled(false)
    }
  }

  const isIdSectionDisabled =
    watch('title') ||
    selectedTypes.length > 0 ||
    selectedLayouts.length > 0 ||
    selectedFromDateTime ||
    selectedToDateTime

  return (
    <>
      <form onSubmit={(e) => onSearch(e)}>
        <MUIBox>
          <div className='rolebox__content-padding'>
            <Grid container columnSpacing={10} rowSpacing={4}>
              <Grid item xs={12} md={12} lg={12} xl={12}>
                <MUIField
                  name='jobName'
                  label='Job Name'
                  type='text'
                  control={control as Control | undefined}
                  error={errors}
                  maxLength={200}
                  required={true}
                  onChange={(e: any) => setValue('jobName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={12} xl={12}>
                <MUISelect
                  name={'targetState'}
                  lable={'Target State'}
                  placeholder={'Target State'}
                  labelKey={'value'}
                  valueKey={'key'}
                  control={control}
                  error={errors}
                  value={getValues('targetState')}
                  options={targateStateOption}
                  onChange={(e) => handleTargetStateOnChange(e)}
                  required={true}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={12} xl={12}>
                <MUISelect
                  name={'item'}
                  lable={'Item'}
                  placeholder={'Item'}
                  labelKey={'value'}
                  valueKey={'key'}
                  value={getValues('item')}
                  control={control}
                  error={errors}
                  options={itemVal}
                  onChange={(e) => {
                    setValue('item', e.target.value)
                  }}
                  required={true}
                />
              </Grid>
            </Grid>
          </div>
        </MUIBox>

        <MUIBox className={`${BulkStyle.parameter_box}`}>
          <div>
            <div className='heading-6 d-flex text-left rolebox__header-padding'>
              Search By Parameter
            </div>
            <div className='rolebox__content-padding'>
              <div className={`${BulkStyle.form_container}`}>
                <div className={`${BulkStyle.form_row}`}>
                  <label className={`${BulkStyle.form_label}`} htmlFor='searchContentId'>
                    ID
                  </label>
                  <MUIField
                    label={'Select'}
                    name={'id'}
                    placeholder={'Id'}
                    type='text'
                    control={control}
                    error={errors}
                    disabled={isIdSectionDisabled}
                    onChange={(e: any) => {
                      const value = e.target.value
                      setValue('id', value)
                      setIsParameterSectionDisabled(value.length > 0)
                      handleOnFormChange()
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '20px 0',
                  }}
                >
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#ccc' }}></div>
                  <span style={{ margin: '0 10px', color: '#666', fontWeight: 'bold' }}>or</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#ccc' }}></div>
                </div>
                <div className={`${BulkStyle.form_row}`}>
                  <label className={`${BulkStyle.form_label}`} htmlFor='title'>
                    {BULK_STATE_CHANGE_FORM.BULK_STATE_FORM.TITLE}
                  </label>
                  <MUIField
                    label={'Title'}
                    name={'title'}
                    placeholder={'Title'}
                    type='text'
                    value={'dummy'}
                    control={control}
                    error={errors}
                    disabled={isParameterSectionDisabled}
                    onChange={(e: any) => {
                      handleOnFormChange()
                    }}
                  />
                </div>
                <div className={`${BulkStyle.form_row}`}>
                  <label className={`${BulkStyle.form_label}`}>
                    {BULK_STATE_CHANGE_FORM.BULK_STATE_FORM.TYPE}
                  </label>
                  <MUIAutocompleteMultiSelect
                    name={'Type'}
                    label={'Type'}
                    labelKey={'value'}
                    disabled={isTypeDropdownDisabled || isParameterSectionDisabled}
                    control={control}
                    error={errors}
                    options={itemTypes}
                    limitTags={2}
                    value={selectedTypes}
                    onInputChange={(e: any, value: any) => {
                      setSelectedTypes(value)
                      handleOnFormChange()
                    }}
                  />
                </div>
                <div className={`${BulkStyle.form_row}`}>
                  <label className={`${BulkStyle.form_label}`}>
                    {BULK_STATE_CHANGE_FORM.BULK_STATE_FORM.LAYOUT}
                  </label>
                  <MUIAutocompleteMultiSelect
                    name={'Layout'}
                    label={'Layout'}
                    labelKey={'value'}
                    disabled={isTypeDropdownDisabled || isParameterSectionDisabled}
                    control={control}
                    error={errors}
                    options={layouts}
                    limitTags={2}
                    value={selectedLayouts}
                    onInputChange={(e: any, value: any) => {
                      setSelectedLayouts(value)
                      handleOnFormChange()
                    }}
                  />
                </div>
                <div className={`${BulkStyle.form_row}`}>
                  <label className={`${BulkStyle.form_label}`}>
                    {BULK_STATE_CHANGE_FORM.BULK_STATE_FORM.STATE}
                  </label>
                  <MUISelectWithoutControl
                    name={'state'}
                    lable={'State'}
                    labelKey={'key'}
                    control={control}
                    error={errors}
                    options={BULK_STATE_CHANGE_FORM.STATE_OPTION}
                    disabled={true}
                    value={selectedState}
                    isDisabled={isParameterSectionDisabled}
                    onChange={() => {
                      handleOnFormChange()
                    }}
                  />
                </div>
                <div className={`${BulkStyle.form_row}`}>
                  <label className={`${BulkStyle.form_label}`}>
                    {BULK_STATE_CHANGE_FORM.BULK_STATE_FORM.LAST_FEATURED_DATE}
                  </label>
                  <div className={`${BulkStyle.featured_date}`}>
                    <MUIDateTimePicker
                      label={'From'}
                      value={selectedFromDateTime}
                      onChange={(date: Date) => handleDateTimeChange('StartDate', date)}
                      error={errors}
                      views={CONSTANTS.DATE_TIME_FORMAT_VIEWS}
                      type={'date'}
                      name={'StartDate'}
                      disableFuture={true}
                      disabled={
                        isParameterSectionDisabled ||
                        getValues('targetState')?.toLowerCase() === 'feature'
                      }
                    />
                    <MUIDateTimePicker
                      label={'To'}
                      value={selectedToDateTime}
                      onChange={(date: Date) => handleDateTimeChange('EndDate', date)}
                      error={errors}
                      views={CONSTANTS.DATE_TIME_FORMAT_VIEWS}
                      type={'date'}
                      name={'EndDate'}
                      disableFuture={true}
                      disabled={
                        isParameterSectionDisabled ||
                        getValues('targetState')?.toLowerCase() === 'feature'
                      }
                    />
                  </div>
                </div>
              </div>
              <div className={`${BulkStyle.advance_search_submit}`}>
                <MUIButton
                  variant='contained'
                  type='submit'
                  className='clone-modal-text'
                  label={ADVANCED_SEARCH.SEARCH_CTA}
                  width={'172px'}
                  disabled={isSearchButtonEnabled}
                />
              </div>
            </div>
          </div>
        </MUIBox>
      </form>
      {Object.keys(searchPayload).length > 0 && (
        <div>
          <Alert
            severity='warning'
            icon={false}
            className='seg-tip-container'
            style={{ marginTop: '20px', border: '1px dashed #FFB300', borderRadius: '5px' }}
          >
            <div style={{ display: 'flex' }}>
              <div className='alert-icon-container'>
                <TipsIcon />
              </div>
              <div className='text-container' style={{ marginLeft: '10px' }}>
                Only 100 records can be selected for bulk operations
              </div>
            </div>
          </Alert>
          <div className={`${BulkStyle.selected_count}`}>
            Selected : {selectedRecords?.length} / {CONSTANTS.MAX_BULK_SELECT_RECORD}
          </div>
          <BulkSearchResult
            searchPayload={searchPayload}
            getSelectedRecords={(records: any) => {
              setSelectedRecords(records)
            }}
          />
          <div className='footer'>
            <div className='submit-btn-wrapper'>
              <div className='submit-btn'>
                <MUIButton
                  size='large'
                  type={'submit'}
                  label={createJobNameBtnName}
                  variant='contained'
                  width={150}
                  disabled={handleButtonDisableByRole() || selectedRecords.length === 0}
                  handleClickCB={() => handleCreateJob()}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <RoleConfirmation
        {...dialogProps}
        handleSuccess={handleConfirmJobCreation}
        handleClose={handleClose}
        cancelButtonLabel='Cancel'
        successButtonLabel='Okay'
      />
    </>
  )
}

export default BulkStateChangeEditorForm
